import { motion } from "framer-motion";
import { Coins } from "lucide-react";
import { useState } from "react";
import { canUseAction, ROLE_ACTIONS } from "../actionRules";
import { anchorRef, ME_COINS, ME_HAND } from "../fxAnchors";
import { ACTIONS, BLOCK_ICON, ROLES } from "../gameData";
import type { ActionType, Influence, Player } from "../types";
import { RoleCard } from "./RoleCard";
import "../handDock.css";

interface Props {
  me: Player;
  disabled: boolean;
  /** The action currently picking a target, so an armed chip can toggle it off. */
  targeting: ActionType | null;
  onTarget: (type: ActionType) => void;
  onCancelTarget: () => void;
  onDeclare: (type: ActionType) => void;
}

/**
 * Your own two cards, fanned and bleeding off the bottom of the screen (that is
 * deliberate), each one wearing its own ability chip on top of it.
 *
 * The chip is a SIBLING of the card inside `.hand-slot`, never a child: nesting
 * a button inside a button is invalid, and the card itself has to stay pressable
 * for hold-to-reveal. The slot carries the fan transform, so the chip inherits
 * the card's rotation and stays visually welded to it.
 */
export function MyHand({
  me,
  disabled,
  targeting,
  onTarget,
  onCancelTarget,
  onDeclare
}: Props) {
  const [heldId, setHeldId] = useState<string | null>(null);
  const cards = me.influence.slice(0, 2);

  const chooseAction = (type: ActionType) => {
    if (disabled || !canUseAction(me, type)) return;
    if (ACTIONS[type].target) {
      if (targeting === type) onCancelTarget();
      else onTarget(type);
    } else {
      onDeclare(type);
    }
  };

  const renderChip = (card: Influence) => {
    if (card.revealed || !card.role) return null;
    const role = ROLES[card.role];
    const type = ROLE_ACTIONS[card.role];

    // Contessa has no action. Rather than leaving a mysterious gap where the
    // other card has a button, it wears a flat, obviously-inert plate that says
    // what it IS good for. Holding the card spells out the full block.
    if (!type) {
      return (
        <span className="hand-ability hand-ability--passive" title={role.blocks}>
          <BLOCK_ICON size={13} strokeWidth={2.3} />
          <span>Blocks</span>
        </span>
      );
    }

    const action = ACTIONS[type];
    const Icon = action.icon;
    const armed = targeting === type;
    return (
      <motion.button
        type="button"
        className={armed ? "hand-ability hand-ability--armed" : "hand-ability"}
        disabled={disabled || !canUseAction(me, type)}
        onClick={() => chooseAction(type)}
        whileTap={{ y: -2, scale: 0.97 }}
        aria-pressed={action.target ? armed : undefined}
        aria-label={`${action.name}, from your ${role.name}`}
      >
        <Icon size={13} strokeWidth={2.3} />
        <span>{action.name}</span>
      </motion.button>
    );
  };

  return (
    <section className={targeting ? "my-court my-court--targeting" : "my-court"}>
      <div className="my-hand" ref={anchorRef(ME_HAND)}>
        {cards.map((card, index) => (
          <div
            key={card.id}
            className={[
              "hand-slot",
              `hand-slot--${index}`,
              heldId === card.id ? "hand-slot--held" : ""
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <RoleCard
              card={card}
              variant="hand"
              onHoldChange={(held) =>
                setHeldId((current) =>
                  held ? card.id : current === card.id ? null : current
                )
              }
            />
            {renderChip(card)}
          </div>
        ))}

        {/* The remount key lives on the NUMBER, never on the badge: keying the
            badge tears it out of the DOM on every coin change, and the anchor
            the coin flight is aiming at would go null exactly when it fires. */}
        <strong className="my-hand__coins" ref={anchorRef(ME_COINS)} aria-label={`${me.coins} Coins`}>
          <Coins size={17} />
          <motion.span
            key={me.coins}
            initial={{ scale: 1.45, y: -3 }}
            animate={{ scale: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 420, damping: 19 }}
          >
            {me.coins}
          </motion.span>
        </strong>
      </div>
    </section>
  );
}
